import { useEffect, useState } from 'react';

import { useTranslation } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { SignDocCardSignedMode, SignDocCardUnsignedMode } from '@bcp-nextgen-dx-component-factory/design-system';
import { useDrawer, useFetchDocument } from '@hooks';
import { avatarLogoBcp } from '@icons';
import { useBankTerms, useServiceResponse } from '@store';

interface BankDocumentHelperResources {
	descriptionSignDocCard: string;
	titleDoc: string;
	attentionRead: string;
	consult: string;
	readAndAccept: string;
}

interface BankDocumentHelperOutput {
	resources: BankDocumentHelperResources;
	signedOrUnSigned: SignDocCardSignedMode | SignDocCardUnsignedMode;
	base64Terms?: string;
	bankTermsDrawerName: string;
	termsWereAccepted?: boolean;
	handleOnClickAcceptTerms: () => void;
}

export const useBankDocumentHelper = (): BankDocumentHelperOutput => {
	const t = useTranslation();
	const bankTermsDrawerName = 'bankTerms';
	const { show: showPdfDrawer, hide: hidePdfDrawer } = useDrawer(bankTermsDrawerName);
	const { acceptTerms, termsWereAccepted } = useBankTerms();
	const { retrieveBankDocumentReference } = useServiceResponse();
	const { getDocumentBase64 } = useFetchDocument();
	const [base64Terms, setBase64Terms] = useState<string | undefined>();

	useEffect(() => {
		if (retrieveBankDocumentReference !== undefined) {
			setBase64Terms(getDocumentBase64(retrieveBankDocumentReference));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [retrieveBankDocumentReference?.documentList?.length]);

	const resources: BankDocumentHelperResources = {
		descriptionSignDocCard: t('placeholders.millennium.documentContract'),
		titleDoc: t('placeholders.millenniumDocument.terms.title'),
		attentionRead: t('general.pdfViewer.attentionRead'),
		consult: t('general.document.actions.consult'),
		readAndAccept: t('general.document.actions.readAndAccept'),
	};

	const handleOnClickReadAndAcceptDocument = () => {
		showPdfDrawer();
	};

	const handleOnClickAcceptTerms = () => {
		acceptTerms();
		hidePdfDrawer();
	};

	const signedProps: SignDocCardSignedMode = {
		mode: 'signed',
		linkProps: {
			middleContent: resources.consult,
			size: 'medium',
			onPress: handleOnClickReadAndAcceptDocument,
		},
	};

	const unsignedProps: SignDocCardUnsignedMode = {
		mode: 'unsigned',
		buttonProps: {
			label: resources.readAndAccept.toUpperCase(),
			onPress: handleOnClickReadAndAcceptDocument,
		},
		image: {
			type: 'avatar',
			avatarProps: {
				source: avatarLogoBcp,
				backgroundColor: 'transparent',
			},
		},
	};

	const signedOrUnSigned: SignDocCardSignedMode | SignDocCardUnsignedMode = termsWereAccepted
		? signedProps
		: unsignedProps;

	return {
		resources,
		signedOrUnSigned,
		base64Terms,
		bankTermsDrawerName,
		termsWereAccepted,
		handleOnClickAcceptTerms,
	};
};
